import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, ImagePlus, CalendarDays, Star, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { F, useConfirm } from "./_shared";

/* ---------------- Events Tab ---------------- */
const CATEGORIES = ["concert", "sports", "wedding", "gala", "corporate", "festival", "nightlife", "other"];

export default function EventsTab() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null); // event object or {} for new
  const { confirm, dialog: confirmDialog } = useConfirm();
  const load = useCallback(() => {
    api.get("/events", { params: { include_past: true } }).then((r) => setEvents(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const featuredCount = events.filter((e) => e.featured).length;

  const remove = async (id, title) => {
    const ok = await confirm({
      title: "Delete event?",
      message: "It will be removed from the calendar and the home page.",
      meta: title,
      confirmLabel: "Delete event",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="bo-events-tab">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="label-caps text-lime-400">Events · Calendar</div>
          <div className="text-xs text-white/50 mt-1">
            Up to <span className="text-lime-400 font-semibold">3 featured events</span> show as clickable flyers on the home page. Currently featured: <span className="text-lime-400 font-semibold">{featuredCount}/3</span>
          </div>
        </div>
        <button
          type="button"
          data-testid="bo-new-event"
          onClick={() => setEditing({})}
          className="bg-lime-400 text-ink-900 rounded-full px-4 py-1.5 text-xs font-semibold label-caps hover:brightness-110 inline-flex items-center gap-1.5">
          <Plus size={12} /> New event
        </button>
      </div>

      {editing !== null && (
        <EventModal
          event={editing?.id ? editing : null}
          featuredCount={featuredCount}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {events.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center text-white/50">
          <CalendarDays size={32} className="mx-auto mb-3 text-lime-400" />
          <div className="font-serif text-lg text-white mb-1">No events yet</div>
          <div className="text-xs">Add your first event to promote chauffeured rides for concerts, weddings, or galas.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((e) => (
            <div key={e.id} data-testid={`bo-event-${e.id}`}
              className="glass rounded-2xl overflow-hidden border border-white/10 hover:border-lime-400/40 transition-colors">
              <div className="aspect-[3/4] bg-ink-800 relative">
                {e.flyer_url ? (
                  <img src={e.flyer_url} alt={e.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    <ImagePlus size={40} />
                  </div>
                )}
                {e.featured && (
                  <span className="absolute top-2 left-2 bg-lime-400 text-ink-900 text-[10px] font-bold label-caps rounded-full px-2 py-1 inline-flex items-center gap-1">
                    <Star size={10} /> Featured
                  </span>
                )}
                {(() => {
                  const past = e.event_date && (new Date(e.event_date).getTime() < Date.now() - 24 * 60 * 60 * 1000);
                  return past ? (
                    <span className="absolute top-2 right-2 bg-white/15 text-white/70 text-[10px] font-bold label-caps rounded-full px-2 py-1 backdrop-blur">
                      Past · hidden from home
                    </span>
                  ) : null;
                })()}
              </div>
              <div className="p-4 space-y-2">
                <div className="label-caps text-lime-400 text-[10px]">{e.category || "event"}</div>
                <div className="font-serif text-lg leading-tight text-white line-clamp-2">{e.title}</div>
                <div className="text-xs text-white/60 flex items-center gap-1">
                  <CalendarDays size={12} className="text-lime-400" />
                  {e.event_date ? new Date(e.event_date).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "TBD"}
                </div>
                {e.location && (
                  <div className="text-xs text-white/60 flex items-center gap-1">
                    <MapPin size={12} className="text-lime-400" /> <span className="line-clamp-1">{e.location}</span>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <button type="button"
                    data-testid={`bo-event-edit-${e.id}`}
                    onClick={() => setEditing(e)}
                    className="flex-1 py-1.5 rounded-full text-[10px] label-caps bg-lime-400 text-ink-900 font-semibold hover:brightness-110">
                    Edit
                  </button>
                  <button type="button"
                    data-testid={`bo-event-delete-${e.id}`}
                    onClick={() => remove(e.id, e.title)}
                    className="py-1.5 px-3 rounded-full text-[10px] label-caps border border-red-400/40 text-red-400/80 hover:bg-red-400/10 inline-flex items-center gap-1">
                    <Trash2 size={10} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

function EventModal({ event, featuredCount, onClose, onSaved }) {
  const isEdit = !!event;
  const [form, setForm] = useState(() => ({
    title: event?.title || "",
    event_date: event?.event_date ? event.event_date.slice(0, 16) : "",
    location: event?.location || "",
    description: event?.description || "",
    category: event?.category || "concert",
    flyer_url: event?.flyer_url || "",
    featured: !!event?.featured,
    display_order: event?.display_order ?? 0,
  }));
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canFeatureMore = featuredCount < 3 || form.featured || (isEdit && event.featured);

  const uploadFlyer = async (fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/events/upload-flyer", fd);
      const absolute = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      set("flyer_url", absolute);
      toast.success("Flyer uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Event title is required");
    if (!form.event_date) return toast.error("Event date is required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        event_date: new Date(form.event_date).toISOString(),
        display_order: Number(form.display_order || 0),
      };
      if (isEdit) {
        await api.patch(`/admin/events/${event.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="bo-event-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-start justify-center p-4 overflow-y-auto"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-2xl bg-ink-900 border border-lime-400/40 rounded-3xl p-6 my-8 space-y-4">
        <div className="flex items-center justify-between">
          <div className="label-caps text-lime-400 text-xs">{isEdit ? "Edit event" : "Create new event"}</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {/* Flyer uploader */}
        <div>
          <div className="label-caps text-lime-400 text-[10px] mb-2">Event flyer</div>
          <input ref={fileRef} type="file" accept="image/*,.heic,.heif" className="hidden"
            data-testid="bo-event-flyer-input"
            onChange={(e) => { uploadFlyer(e.target.files); e.target.value = ""; }} />
          <div
            data-testid="bo-event-flyer-dropzone"
            onClick={() => !uploading && fileRef.current?.click()}
            className={`rounded-2xl border-2 border-dashed cursor-pointer transition-all overflow-hidden ${form.flyer_url ? "border-white/15" : "border-lime-400/60 bg-lime-400/5 hover:border-lime-400"}`}
          >
            {form.flyer_url ? (
              <div className="relative aspect-[3/4] max-h-72 mx-auto bg-ink-800">
                <img src={form.flyer_url} alt="flyer" className="w-full h-full object-cover" />
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); set("flyer_url", ""); }}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-ink-950/80 hover:bg-red-500/70 text-white flex items-center justify-center">
                  <X size={14} />
                </button>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink-950/80 to-transparent p-3 text-center">
                  <span className="text-[10px] text-white/70">Tap to replace flyer</span>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <ImagePlus size={32} className="mx-auto mb-2 text-lime-400" />
                <div className="text-sm text-white">{uploading ? "Uploading…" : "Tap to upload event flyer"}</div>
                <div className="text-[10px] text-white/50 mt-1">Portrait 3:4 works best (concert-poster style). JPG/PNG/HEIC.</div>
              </div>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <F label="Title *">
            <input required data-testid="bo-event-title" value={form.title}
              onChange={(e) => set("title", e.target.value)} className="nv-input" placeholder="Beyoncé at MetLife" />
          </F>
          <F label="Category">
            <select data-testid="bo-event-category" value={form.category}
              onChange={(e) => set("category", e.target.value)} className="nv-input">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
            </select>
          </F>
          <F label="Date & time *">
            <input type="datetime-local" required data-testid="bo-event-date"
              value={form.event_date} onChange={(e) => set("event_date", e.target.value)}
              className="nv-input" />
          </F>
          <F label="Location">
            <input data-testid="bo-event-location" value={form.location}
              onChange={(e) => set("location", e.target.value)} className="nv-input"
              placeholder="MetLife Stadium, East Rutherford NJ" />
          </F>
          <F label="Display order (lower = first)">
            <input type="number" data-testid="bo-event-order" value={form.display_order}
              onChange={(e) => set("display_order", e.target.value)} className="nv-input" />
          </F>
          <F label="Featured on home page">
            <label className="inline-flex items-center gap-2 text-xs text-white/80 cursor-pointer h-full py-2">
              <input type="checkbox" data-testid="bo-event-featured"
                checked={form.featured} disabled={!canFeatureMore}
                onChange={(e) => set("featured", e.target.checked)} className="accent-lime-400" />
              <span>{canFeatureMore ? "Show as clickable flyer on home" : "Max 3 featured reached — un-feature another first"}</span>
            </label>
          </F>
        </div>

        <F label="Description">
          <textarea data-testid="bo-event-description" value={form.description} rows={3}
            onChange={(e) => set("description", e.target.value)}
            className="nv-input" placeholder="Short blurb shown on the event page." />
        </F>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/12 text-white/60 hover:border-white/30">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            data-testid="bo-event-save"
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-lime-400 text-ink-900 font-semibold hover:brightness-110 disabled:opacity-50">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create event"}
          </button>
        </div>
      </form>
      <style>{`
        .nv-input {
          background: #0f0f11; border: 1px solid rgba(255,255,255,0.08);
          color: #fff; border-radius: 10px; padding: 10px 12px;
          width: 100%; font-size: 13px;
        }
        .nv-input:focus { outline: none; border-color: #A3E635; }
      `}</style>
    </div>
  );
}

